import { DefaultMap, definedMap } from "@glideapps/ts-necessities";
import type { ComponentIndexes } from "common/component-indexes";
import { AppKind } from "common/components/app-kind";
import { SettableForwardingValueProducer } from "common/components/component-helpers";
import { Data } from "common/components/data";
import { setMonkeyPatchEnforcer } from "common/components/datastore/indexeddb";
import { LocalDataStore } from "common/components/datastore/local-datastore";
import { RealEmailAddressValueProducer, VerifiedEmailAddressValueProducer } from "common/components/functions-glide";
import type { IconImage } from "common/components/icon-image";
import type { AppAuthentication, AppFeatures, ScreenFlag } from "common/components/SerializedApp";
import type { TableIndex } from "common/components/table-index";
import type {
    AppEnvironment,
    AppEnvironmentDelegate,
    AppFacilities,
    AppOther,
    AppUserAuthenticator,
    ColumnFormatChangedCallback,
    ComponentMoveAction,
    Context,
    DataStore,
    EnvironmentBoundEvaluable,
    Evaluable,
    OfflineQueue,
    RenderEnvironment,
    RootEvalEnvironment,
    UploadProgressHandler,
    UploadSession,
    UserProfileTableInfo,
    ValueProducer,
} from "common/components/types";
import { ComponentHighlight } from "common/components/types";
import type { Database, PaymentInformationForBuyButtons } from "common/Database";
import type { TableName } from "common/database-strings";
import type { EminenceFlags } from "common/Database/eminence";
import { freeFlags, getBaseRowQuota } from "common/Database/eminence";
import type { AllQuotaValues } from "common/Database/quotas";
import { QuotaKind } from "common/Database/quotas";
import { logError } from "common/debug-print";
import type { Formula, TableColumn, TypeSchema } from "common/description";
import { GlobalVariableNames } from "common/description";
import type { ReportGeocodesInAppBody, UploadComponentKind } from "common/firebase-function-types";
import type { LocationSettings } from "common/location-common";
import { DeviceFormFactor } from "common/render/form-factor";
import type { Sound } from "common/sound";
import type { SourceMetadata } from "common/source-metadata";
import { defined, definedMapWithDefault } from "common/Support";
import type { YesCodeValue } from "common/yes-code-types";
import { modifyColumnsInSchema } from "generator/computed-columns";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { signOutAppUser } from "webapp/lib/sign-out";

import { HeartbeatAndQuotaManagerBase } from "./heartbeat-and-quota";
import { forceIndexedDBReplacementIfNecessary } from "./indexeddb-hacks";
import { uploadAppFile } from "./upload-app-file";

const reportQuotaIncreaseDebouncePeriod = 2000;

setMonkeyPatchEnforcer(forceIndexedDBReplacementIfNecessary);

export class ForwardingAppEnvironment implements AppEnvironment {
    private _delegate: AppEnvironmentDelegate | undefined;
    private readonly _highlightComponentFlags: Map<string, Data<AppOther, AppEnvironment>> = new Map();
    private _inFlightGeocodes: number = 0;
    private readonly _geocodeCounts: DefaultMap<string, number> = new DefaultMap(() => 0);
    private _geocodeQuotaReachedFlagValueProducer: SettableForwardingValueProducer<
        AppOther,
        AppEnvironment
    > = new SettableForwardingValueProducer();
    private readonly _geocodeQuotaReachedForKeyValueProducers: DefaultMap<
        string,
        SettableForwardingValueProducer<AppOther, AppEnvironment>
    > = new DefaultMap(() => new SettableForwardingValueProducer());
    private _localDataStore: LocalDataStore | undefined;
    private _heartbeatAndQuotaManager: HeartbeatAndQuotaManagerBase;
    // We're assuming each callback only has one column format it's registering for.
    private readonly _columnFormatCallbacksToRegister: Map<
        ColumnFormatChangedCallback,
        [TableName, string]
    > = new Map();
    public readonly globalVariables: DefaultMap<string, ValueProducer> = new DefaultMap(n =>
        this.makeGlobalVariable(n)
    );
    public readonly globalValues: Map<string, ValueProducer> = new Map();
    public readonly tableIndexes: Map<string, TableIndex<AppOther, AppEnvironment>> = new Map();
    public readonly fallbackUsername: ValueProducer = new Data<AppOther, AppEnvironment>(undefined);

    private readonly _quotaReachedValueProducers: DefaultMap<
        QuotaKind,
        SettableForwardingValueProducer<AppOther, AppEnvironment>
    > = new DefaultMap(() => new SettableForwardingValueProducer());

    constructor(
        public readonly authenticator: AppUserAuthenticator,
        private readonly _appFeatures: AppFeatures,
        public readonly database: Database,
        public readonly dataStore: DataStore,
        public readonly isBuilder: boolean,
        private readonly _defaultSourceMetadata: readonly SourceMetadata[] | undefined,
        private readonly _defaultUserProfileTableInfo: UserProfileTableInfo | undefined,
        // FIXME: Move this into a global component variable, too
        public readonly appIcon: IconImage
    ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const appEnvironment = this;

        class AppEnvironmentHeartbeatAndQuotaManager extends HeartbeatAndQuotaManagerBase {
            constructor() {
                super(appEnvironment.appID, appEnvironment.appFacilities, appEnvironment.database);
            }

            protected getRowQuotaOverride(): number | undefined {
                return getBaseRowQuota(appEnvironment.eminenceFlags);
            }

            protected getEminenceFlags(): EminenceFlags {
                return appEnvironment.eminenceFlags;
            }

            protected quotasDidUpdate(newQuotas: AllQuotaValues, oldQuotas: AllQuotaValues): void {
                appEnvironment._delegate?.quotasDidUpdate?.(newQuotas, oldQuotas);
            }

            protected updateQuotaReached(kind: QuotaKind, reached: boolean): void {
                const vp = appEnvironment._quotaReachedValueProducers.get(kind);
                if (vp.forwardee !== undefined) {
                    vp.set(reached);
                } else {
                    vp.setForwardee(new Data<AppOther, AppEnvironment>(reached));
                }
            }
        }

        this._heartbeatAndQuotaManager = new AppEnvironmentHeartbeatAndQuotaManager();
    }

    public get appKind(): AppKind {
        return AppKind.App;
    }

    public get appFacilities(): AppFacilities {
        return this.authenticator.appFacilities;
    }

    public get appID(): string {
        return this.authenticator.appID;
    }

    public get appUserID(): string | undefined {
        return this.authenticator.appUserID;
    }

    public get locationSettings(): LocationSettings | undefined {
        return this._delegate?.locationSettings;
    }

    public setDelegate(delegate: AppEnvironmentDelegate | undefined): void {
        this._delegate = delegate;
        if (delegate !== undefined) {
            delegate.updateGlobalVariables(this.globalVariables);

            for (const [callback, [tableName, columnName]] of this._columnFormatCallbacksToRegister) {
                delegate.registerColumnFormatChangedCallback(tableName, columnName, callback);
                callback();
            }
            this._columnFormatCallbacksToRegister.clear();
        }
        this.dataStore.appEnvironmentUpdated();
    }

    public get shortName(): string | undefined {
        return definedMap(this._delegate, cb => cb.shortName);
    }

    public get customDomain(): string | undefined {
        return definedMap(this._delegate, cb => cb.customDomain);
    }

    public get isPublished(): boolean {
        return definedMapWithDefault(this._delegate, false, cb => cb.isPublished);
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        if (this._delegate === undefined) return this._defaultUserProfileTableInfo;
        return this._delegate.userProfileTableInfo;
    }

    public get sourceMetadata(): readonly SourceMetadata[] | undefined {
        if (this._delegate === undefined) return this._defaultSourceMetadata;
        return this._delegate.sourceMetadata;
    }

    public get localDataStore(): DataStore {
        if (this._localDataStore === undefined) {
            this._localDataStore = new LocalDataStore(this.appID);
        }
        return this._localDataStore;
    }

    public get enableShareScreen(): boolean {
        return this.eminenceFlags.canDisableShare !== true || this.appFeatures.enableShareScreen === true;
    }

    public get shareScreenTitle(): string {
        return this.appFeatures.shareScreenTitle ?? "";
    }

    public pushBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        env: RenderEnvironment,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushBuilderScreen(name, ctx, title, env, flags, itemID);
    }

    public pushModalBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushModalBuilderScreen(name, ctx, title, flags);
    }

    public pushMenuScreen(
        menuID: string,
        primaryKey: string,
        defaultScreenName: string,
        ctx: Context,
        env: RenderEnvironment
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushMenuScreen(menuID, primaryKey, defaultScreenName, ctx, env);
    }

    public popScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popScreen(env);
    }

    public popModalScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popModalScreen(env);
    }

    public popAnyScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popAnyScreen(env);
    }

    public popToRoot(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popToRoot(env);
    }

    public pushUserProfileScreen(): void {
        if (this._delegate === undefined) return;
        this._delegate.pushUserProfileScreen();
    }

    public setCurrentTab(index: number, env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.setCurrentTab(index, env);
    }

    public get currentPivot(): number {
        if (this._delegate === undefined) return 0;
        return this._delegate.currentPivot;
    }

    public setCurrentPivot(index: number, env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.setCurrentPivot(index, env);
    }

    public getHighlightComponentFlag(componentID: string): ValueProducer {
        let flag = this._highlightComponentFlags.get(componentID);
        if (flag === undefined) {
            flag = new Data<AppOther, AppEnvironment>(ComponentHighlight.None);
            this._highlightComponentFlags.set(componentID, flag);
        }
        return flag;
    }

    public componentMoved(sourceComponentID: string, targetComponentID: string, action: ComponentMoveAction): void {
        this._delegate?.componentMoved(sourceComponentID, targetComponentID, action);
    }

    public showToast(message: string, isSuccess: boolean): void {
        if (this._delegate === undefined) return;
        return this._delegate.showToast(message, isSuccess);
    }

    public playSound(name: Sound): void {
        return this._delegate?.playSound(name);
    }

    public copyToClipboard(str: string): void {
        return this._delegate?.copyToClipboard(str);
    }

    public signOut(): void {
        signOutAppUser(this.appID);
    }

    public get paymentInformation(): PaymentInformationForBuyButtons {
        if (this._delegate === undefined) return {};
        return this._delegate.paymentInformation;
    }

    public get demonstratingProComponents(): boolean {
        if (this._delegate === undefined) return false;
        return this._delegate.demonstratingProComponents;
    }

    public get appFeatures(): AppFeatures {
        return definedMapWithDefault(this._delegate, this._appFeatures, cb => cb.modifyAppFeatures(this._appFeatures));
    }

    public get eminenceFlags(): EminenceFlags {
        return definedMap(this._delegate, cb => cb.eminenceFlags) ?? freeFlags;
    }

    public get authentication(): AppAuthentication | undefined {
        return definedMap(this._delegate, cb => cb.authentication);
    }

    public get deviceFormFactor(): DeviceFormFactor {
        return definedMapWithDefault(this._delegate, DeviceFormFactor.Phone, cb => cb.deviceFormFactor);
    }

    public getComputedColumn(
        schema: TypeSchema,
        tableName: TableName,
        columnName: string,
        formula: Formula,
        isBuilder: boolean,
        forDataEditor: boolean
    ): Evaluable | undefined {
        if (this._delegate === undefined) {
            logError("Missing delegate when calling getComputedColumn");
            return undefined;
        }
        return this._delegate.getComputedColumn?.(schema, tableName, columnName, formula, isBuilder, forDataEditor);
    }

    public modifyColumnsInSchema(
        oldSchema: TypeSchema,
        removed: ReadonlyMap<string, readonly string[]>,
        modified: ReadonlyMap<string, readonly TableColumn[]>,
        moved: ReadonlyMap<string, readonly [string, number][]>,
        setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
        rowIDColumns: ReadonlyMap<string, string | undefined>
    ): { schema: TypeSchema; errors: readonly string[] } {
        return modifyColumnsInSchema(
            oldSchema,
            this.sourceMetadata,
            this.userProfileTableInfo,
            removed,
            modified,
            moved,
            setProtected,
            rowIDColumns,
            true
        );
    }

    public getHighestGeocodeCount(): number {
        let highestCount = 0;
        for (const count of this._geocodeCounts.values()) {
            if (count > highestCount) {
                highestCount = count;
            }
        }
        return highestCount;
    }

    private reportGeocodesAfterDebounce = async () => {
        const geocodes = this._inFlightGeocodes;
        if (geocodes === 0) return;

        const { appID } = this;
        this._inFlightGeocodes = 0;
        const body: ReportGeocodesInAppBody = { appID, geocodes };
        await callCloudFunctionWeb("reportGeocodesInApp", body, {});
    };

    public reportGeocode(): void {
        const { _inFlightGeocodes: prevInFlightGeocodes } = this;
        this._inFlightGeocodes++;
        if (prevInFlightGeocodes === 0) {
            setTimeout(this.reportGeocodesAfterDebounce, reportQuotaIncreaseDebouncePeriod);
        }
    }

    private outstandingQuotaUpdates: Partial<Record<QuotaKind, number>> = {};

    private reportingQuotaIncrease: boolean = false;
    private reportQuotaIncreaseAfterDebounce = async () => {
        if (this.reportingQuotaIncrease === true) return;

        while (Object.keys(this.outstandingQuotaUpdates).length > 0) {
            this.reportingQuotaIncrease = true;

            const quotaChanges = this.outstandingQuotaUpdates;
            this.outstandingQuotaUpdates = {};

            try {
                await this.appFacilities.callAuthIfAvailableCloudFunction(
                    "reportQuotaIncreases",
                    { appID: this.appID, quotaChanges },
                    {}
                );
            } finally {
                // FIXME: Should we merge the quota updates back into the "global"
                // instance in the event of a call failure?

                this.reportingQuotaIncrease = false;
            }
        }
    };

    public reportQuotaIncrease(kind: QuotaKind, increment: number): void {
        const prevUpdate = this.outstandingQuotaUpdates[kind] ?? 0;
        this.outstandingQuotaUpdates[kind] = prevUpdate + increment;
        setTimeout(this.reportQuotaIncreaseAfterDebounce, reportQuotaIncreaseDebouncePeriod);
    }

    private getGeocodeQuota(): number {
        return this.eminenceFlags.quotas[QuotaKind.Geocodes].prepaidUsage;
    }

    private setGeocodeQuotaReachedFlag(): void {
        if (this._delegate === undefined) return;

        const quota = this.getGeocodeQuota();

        let count = 0;
        for (const [key, n] of this._geocodeCounts.entries()) {
            count = Math.max(count, n);

            if (!this._geocodeQuotaReachedForKeyValueProducers.has(key)) continue;

            const isReachedForKey = n > quota;
            const vp = this._geocodeQuotaReachedForKeyValueProducers.get(key);
            if (vp.forwardee === undefined) {
                vp.setForwardee(new Data<AppOther, AppEnvironment>(isReachedForKey));
            } else {
                vp.set(isReachedForKey);
            }
        }
        const isQuotaReached = count > quota;
        if (this._geocodeQuotaReachedFlagValueProducer.forwardee === undefined) {
            this._geocodeQuotaReachedFlagValueProducer.setForwardee(new Data<AppOther, AppEnvironment>(isQuotaReached));
        } else {
            this._geocodeQuotaReachedFlagValueProducer.set(isQuotaReached);
        }
    }

    public isGeocodeWithinQuota(key: string): boolean {
        const c = this._geocodeCounts.get(key);
        return c < this.getGeocodeQuota();
    }

    public countGeocodeForQuota(key: string): void {
        this._geocodeCounts.update(key, c => c + 1);
        this.setGeocodeQuotaReachedFlag();
    }

    public getGeocodeQuotaReachedFlag(): ValueProducer {
        return this._geocodeQuotaReachedFlagValueProducer;
    }

    public getGeocodeQuotaReachedForKeyFlag(key: string): ValueProducer {
        return this._geocodeQuotaReachedForKeyValueProducers.get(key);
    }

    public getQuotaReachedFlag(kind: QuotaKind): ValueProducer {
        return this._quotaReachedValueProducers.get(kind);
    }

    public suppressNotificationTopic(topic: string): void {
        if (this._delegate === undefined) return;
        this._delegate.suppressNotificationTopic(topic);
    }

    public unsuppressNotificationTopic(topic: string): void {
        if (this._delegate === undefined) return;
        this._delegate.unsuppressNotificationTopic(topic);
    }

    public isMounted(inEditor: boolean): void {
        this._heartbeatAndQuotaManager.isMounted(inEditor);
    }

    public isUnmounted(inEditor: boolean): void {
        this._heartbeatAndQuotaManager.isUnmounted(inEditor);
    }

    public registerColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void {
        if (this._delegate === undefined) {
            this._columnFormatCallbacksToRegister.set(callback, [tableName, columnName]);
            return;
        }
        this._delegate.registerColumnFormatChangedCallback(tableName, columnName, callback);
    }

    public unregisterColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void {
        if (this._delegate === undefined) {
            this._columnFormatCallbacksToRegister.delete(callback);
            return;
        }
        this._delegate.unregisterColumnFormatChangedCallback(tableName, columnName, callback);
    }

    public getColumnFormat(tableName: TableName, columnName: string): EnvironmentBoundEvaluable | undefined {
        if (this._delegate === undefined) return undefined;
        return this._delegate.getColumnFormat(tableName, columnName);
    }

    public pushFeedbackScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return undefined;
        return this._delegate.pushFeedbackScreen(env);
    }

    public uploadAppFile(
        file: File,
        component: UploadComponentKind,
        onProgress: UploadProgressHandler | undefined
    ): UploadSession {
        const { appID, appFacilities, appKind } = this;
        return uploadAppFile(appID, appKind, appFacilities, file, component, onProgress, () => {
            if (this._delegate === undefined) return;
            const renv = this.makeRenderEnvironment();
            if (renv !== undefined) {
                this.pushFeedbackScreen(renv);
            }
        });
    }

    // ##makeGlobalVariable:
    // FIXME: This is duplicated in the dummy environment, and probably
    // shouldn't be in this class.
    private makeGlobalVariable(variableName: string): ValueProducer {
        if (variableName === GlobalVariableNames.VerifiedEmailAddress) {
            return new VerifiedEmailAddressValueProducer(this);
        }
        if (variableName === GlobalVariableNames.RealEmailAddress) {
            return new RealEmailAddressValueProducer(this);
        }

        const vp = this._delegate?.makeGlobalVariable(variableName);
        if (vp !== undefined) {
            return vp;
        }

        // Global variables have to be loading until they're set, not
        // `undefined`.  One case where this is particularly relevant is the
        // user profile table name.  If it were `undefined` at start, when we
        // try to load the user profile row, the fetch row value producer
        // would return `undefined`, making it seem as if the user profile row
        // is not present, as opposed to not loaded yet.  That in turn leads
        // to ##tabVisibility becoming screwed up.
        return new SettableForwardingValueProducer<AppOther, AppEnvironment>();
    }

    public updateGlobalVariables(): void {
        if (this._delegate === undefined) return undefined;

        this._delegate.updateGlobalVariables(this.globalVariables);
    }

    public makeRootEvalEnvironment(): RootEvalEnvironment {
        return defined(this._delegate?.makeRootEvalEnvironment?.());
    }

    public makeRenderEnvironment(): RenderEnvironment | undefined {
        return this._delegate?.makeRenderEnvironment();
    }

    public addImageComponent(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        imageURL: string
    ): void {
        this._delegate?.addImageComponent(screenName, componentIndex, imageURL);
    }

    public addComponentForColumn(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        columnName: string
    ): void {
        this._delegate?.addComponentForColumn(screenName, componentIndex, columnName);
    }

    public addComponentWithUniqueName(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        uniqueName: string
    ): void {
        this._delegate?.addComponentWithUniqueName(screenName, componentIndex, uniqueName);
    }

    public startBuilderBusy(): void {
        this._delegate?.startBuilderBusy();
    }

    public stopBuilderBusy(): void {
        this._delegate?.stopBuilderBusy();
    }

    public makeOfflineQueue<T>(
        name: string,
        onOnline: () => void,
        idForItem: (item: T) => string
    ): OfflineQueue<T> | undefined {
        return this._delegate?.makeOfflineQueue?.(name, onOnline, idForItem);
    }

    public callYesCode(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
        return this._delegate?.callYesCode?.(url, params);
    }
}
